/**
 * Synthetic "spark / zap" sound via Web Audio API.
 * 
 * Zero external dependencies, zero asset download — generated in ~80ms of audio.
 * Combines a quick noise burst + a descending sine sweep + a tiny high-pitched
 * click to give an electric "zap" that matches the menu's cyan shockwave visual.
 * 
 * Respects prefers-reduced-motion as a proxy for users who've opted out of
 * flashy effects (most accessibility settings also silence decorative audio).
 */

let _audioCtx = null;
let _audioUnlocked = false;
let _pendingStartup = false;

const getCtx = () => {
  if (_audioCtx) return _audioCtx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _audioCtx = new AC();
  } catch {
    return null;
  }
  return _audioCtx;
};

/**
 * Install a one-time global listener so that ANY first user interaction
 * (tap, click, keypress, scroll) unlocks the AudioContext. Once unlocked,
 * if a startup sound was requested while the context was suspended, we
 * replay it so the user still gets the splash audio.
 */
const ensureUnlockListener = () => {
  if (typeof document === 'undefined') return;
  if (ensureUnlockListener._installed) return;
  ensureUnlockListener._installed = true;
  
  const unlock = () => {
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => {
        _audioUnlocked = true;
        if (_pendingStartup) {
          _pendingStartup = false;
          try { playStartupSound(); } catch {}
        }
      }).catch(() => {});
    } else {
      _audioUnlocked = true;
      if (_pendingStartup) {
        _pendingStartup = false;
        try { playStartupSound(); } catch {}
      }
    }
  };
  
  const opts = { once: true, capture: true, passive: true };
  ['pointerdown', 'touchstart', 'click', 'keydown', 'scroll'].forEach(ev => {
    document.addEventListener(ev, unlock, opts);
  });
};

export const playSparkSound = () => {
  // Honor users who've opted out of motion/animation
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const ctx = getCtx();
  if (!ctx) return;
  
  // Some browsers start the context suspended until a user gesture — resume on click
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.22; // overall volume — subtle, not jarring
  master.connect(ctx.destination);
  
  // 1) Short noise burst with band-pass → the initial "crackle"
  const noiseBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.12), ctx.sampleRate);
  const noiseData = noiseBuf.getChannelData(0);
  for (let i = 0; i < noiseData.length; i++) {
    // White noise with exponential decay so it feels like a real spark
    noiseData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / noiseData.length, 2);
  }
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 2200;
  noiseFilter.Q.value = 2.5;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.55, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.11);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(master);
  noise.start(now);
  noise.stop(now + 0.12);
  
  // 2) Descending sine sweep — gives the "zap" its pitch signature
  const sweep = ctx.createOscillator();
  sweep.type = 'sawtooth';
  sweep.frequency.setValueAtTime(1800, now);
  sweep.frequency.exponentialRampToValueAtTime(240, now + 0.08);
  const sweepGain = ctx.createGain();
  sweepGain.gain.setValueAtTime(0.28, now);
  sweepGain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
  sweep.connect(sweepGain);
  sweepGain.connect(master);
  sweep.start(now);
  sweep.stop(now + 0.1);
  
  // 3) High-pitched tick — adds the sharp "electric" edge on top
  const tick = ctx.createOscillator();
  tick.type = 'square';
  tick.frequency.setValueAtTime(3600, now);
  tick.frequency.exponentialRampToValueAtTime(1200, now + 0.03);
  const tickGain = ctx.createGain();
  tickGain.gain.setValueAtTime(0.08, now);
  tickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.035);
  tick.connect(tickGain);
  tickGain.connect(master);
  tick.start(now);
  tick.stop(now + 0.04);
};

/**
 * Soft "whoosh" sound for menu close — a descending filtered noise sweep
 * that feels like air rushing back in. Pairs with the spark-open sound.
 */
export const playWhoosh = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.18;
  master.connect(ctx.destination);
  
  // White-noise buffer ~220ms with a smooth envelope
  const dur = 0.22;
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) {
    const env = Math.sin((i / d.length) * Math.PI); // soft attack + release
    d[i] = (Math.random() * 2 - 1) * env;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  
  // Band-pass that sweeps HIGH → LOW (opposite of spark) so it feels like "closing"
  const band = ctx.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.setValueAtTime(2800, now);
  band.frequency.exponentialRampToValueAtTime(380, now + dur);
  band.Q.value = 1.8;
  
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.linearRampToValueAtTime(0.55, now + 0.04);
  gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
  
  noise.connect(band);
  band.connect(gain);
  gain.connect(master);
  noise.start(now);
  noise.stop(now + dur + 0.02);
  
  // A subtle low sine "thump" at the end for closure weight
  const thump = ctx.createOscillator();
  thump.type = 'sine';
  thump.frequency.setValueAtTime(140, now + 0.08);
  thump.frequency.exponentialRampToValueAtTime(60, now + 0.22);
  const tg = ctx.createGain();
  tg.gain.setValueAtTime(0.001, now + 0.08);
  tg.gain.exponentialRampToValueAtTime(0.22, now + 0.11);
  tg.gain.exponentialRampToValueAtTime(0.001, now + 0.24);
  thump.connect(tg);
  tg.connect(master);
  thump.start(now + 0.08);
  thump.stop(now + 0.25);
};

/**
 * Synthetic "car engine rev" sound for menu item taps.
 * ~320ms. Layered sawtooth oscillators with a sub-octave + low-frequency
 * rumble create a punchy rev, then quickly drop off.
 */
export const playEngineRev = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.20;
  master.connect(ctx.destination);
  
  // Shared low-pass so the rev feels "inside an engine bay" rather than thin
  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.setValueAtTime(900, now);
  lowpass.frequency.linearRampToValueAtTime(1800, now + 0.15);
  lowpass.frequency.linearRampToValueAtTime(700, now + 0.32);
  lowpass.Q.value = 3.5;
  lowpass.connect(master);
  
  // 1) Primary sawtooth — rises in pitch (rev), then falls
  const osc1 = ctx.createOscillator();
  osc1.type = 'sawtooth';
  osc1.frequency.setValueAtTime(85, now);
  osc1.frequency.linearRampToValueAtTime(220, now + 0.12);
  osc1.frequency.linearRampToValueAtTime(140, now + 0.32);
  const g1 = ctx.createGain();
  g1.gain.setValueAtTime(0.001, now);
  g1.gain.exponentialRampToValueAtTime(0.55, now + 0.03);
  g1.gain.exponentialRampToValueAtTime(0.001, now + 0.33);
  osc1.connect(g1);
  g1.connect(lowpass);
  osc1.start(now);
  osc1.stop(now + 0.35);
  
  // 2) Sub-octave square — adds weight/growl
  const osc2 = ctx.createOscillator();
  osc2.type = 'square';
  osc2.frequency.setValueAtTime(55, now);
  osc2.frequency.linearRampToValueAtTime(130, now + 0.12);
  osc2.frequency.linearRampToValueAtTime(70, now + 0.32);
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.001, now);
  g2.gain.exponentialRampToValueAtTime(0.30, now + 0.03);
  g2.gain.exponentialRampToValueAtTime(0.001, now + 0.33);
  osc2.connect(g2);
  g2.connect(lowpass);
  osc2.start(now);
  osc2.stop(now + 0.35);
  
  // 3) Low-frequency noise rumble — the "exhaust" texture
  const noiseBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.32), ctx.sampleRate);
  const nd = noiseBuf.getChannelData(0);
  for (let i = 0; i < nd.length; i++) {
    nd[i] = (Math.random() * 2 - 1) * Math.pow(1 - Math.abs(i / nd.length - 0.35) * 2.2, 1.3);
  }
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  const noiseBand = ctx.createBiquadFilter();
  noiseBand.type = 'bandpass';
  noiseBand.frequency.value = 180;
  noiseBand.Q.value = 1.2;
  const ng = ctx.createGain();
  ng.gain.value = 0.35;
  noise.connect(noiseBand);
  noiseBand.connect(ng);
  ng.connect(lowpass);
  noise.start(now);
  noise.stop(now + 0.33);
};

/**
 * Cinematic "startup / boot" sound for the Welcome splash screen.
 * ~2 seconds. Think: Tesla power-on / sci-fi OS startup chime:
 *   - Slow rising swell pad (two detuned sines) — builds anticipation
 *   - Bright chime burst at ~0.8s — the "logo reveal" moment
 *   - Warm sub-bass pulse — grounds the swell
 *   - Soft airy noise sweep — adds polish
 * Designed to peak right when the brand text animates in.
 */
export const playStartupSound = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  
  // Ensure a first-gesture unlock listener is installed so that if the
  // AudioContext is currently blocked by browser autoplay policy, the
  // sound will replay the moment the user taps/scrolls/clicks anything.
  ensureUnlockListener();
  
  const ctx = getCtx();
  if (!ctx) return;
  
  if (ctx.state === 'suspended') {
    _pendingStartup = true;
    ctx.resume().catch(() => {});
    // If resume() doesn't complete immediately (iOS Safari requires a user
    // gesture on the call stack), the unlock listener will replay on first tap.
    if (ctx.state === 'suspended') return;
    _pendingStartup = false;
  }
  
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.22;
  master.connect(ctx.destination);
  
  // Gentle reverb-ish soft tail via a short feedback delay
  const delay = ctx.createDelay(1.5);
  delay.delayTime.value = 0.18;
  const delayFeedback = ctx.createGain();
  delayFeedback.gain.value = 0.28;
  const delayMix = ctx.createGain();
  delayMix.gain.value = 0.35;
  delay.connect(delayFeedback);
  delayFeedback.connect(delay);
  delay.connect(delayMix);
  delayMix.connect(master);
  
  // 1) Rising swell pad — two detuned sines for richness (A3 → E5, 1.2s swell)
  const makeSwell = (startHz, endHz, detune, gainPeak, startT, endT) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(startHz, startT);
    osc.frequency.exponentialRampToValueAtTime(endHz, endT);
    osc.detune.value = detune;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, startT);
    g.gain.exponentialRampToValueAtTime(gainPeak, endT - 0.15);
    g.gain.exponentialRampToValueAtTime(0.001, endT + 0.3);
    osc.connect(g);
    g.connect(master);
    g.connect(delay); // feeds the reverb tail too
    osc.start(startT);
    osc.stop(endT + 0.35);
  };
  makeSwell(220, 659, 0,   0.25, now,        now + 1.15); // A3 → E5
  makeSwell(220, 659, 14,  0.18, now + 0.02, now + 1.18); // slight detune second voice
  makeSwell(110, 330, -12, 0.14, now + 0.04, now + 1.2);  // sub-octave
  
  // 2) Chime burst at ~0.85s — brings the "peak" moment (E5, B5, E6)
  const chime = (freq, startT, dur, gain) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.001, startT);
    g.gain.exponentialRampToValueAtTime(gain, startT + 0.015);
    g.gain.exponentialRampToValueAtTime(0.001, startT + dur);
    osc.connect(g);
    g.connect(master);
    g.connect(delay);
    osc.start(startT);
    osc.stop(startT + dur + 0.05);
  };
  chime(659.25, now + 0.82, 0.9,  0.22); // E5
  chime(987.77, now + 0.90, 0.8,  0.16); // B5
  chime(1318.5, now + 0.98, 0.7,  0.12); // E6
  
  // 3) Soft airy noise sweep — opens with the swell
  const noiseBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 1.2), ctx.sampleRate);
  const nd = noiseBuf.getChannelData(0);
  for (let i = 0; i < nd.length; i++) {
    const env = Math.sin((i / nd.length) * Math.PI); // gentle bell curve
    nd[i] = (Math.random() * 2 - 1) * env * 0.4;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  const hpf = ctx.createBiquadFilter();
  hpf.type = 'highpass';
  hpf.frequency.setValueAtTime(400, now);
  hpf.frequency.linearRampToValueAtTime(3000, now + 1.0);
  const ng = ctx.createGain();
  ng.gain.value = 0.12;
  noise.connect(hpf);
  hpf.connect(ng);
  ng.connect(master);
  noise.start(now);
  noise.stop(now + 1.3);
  
  // 4) Final soft tail decay — mute delay feedback so it doesn't ring forever
  master.gain.setValueAtTime(0.22, now + 1.4);
  master.gain.exponentialRampToValueAtTime(0.001, now + 2.1);
};

